import React from 'react';

import dayjs from 'dayjs';
// TODO реализовать внутри проекта
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';

import Button from 'epn-ui/umd/dist/components/Button';
import DatePickerRange from 'epn-ui/umd/dist/components/DatePicker/Range';
import Input from 'epn-ui/umd/dist/components/InputText';
import Radio from 'epn-ui/umd/dist/components/Radio';
import Select from 'epn-ui/umd/dist/components/Select';
import Skeleton from 'epn-ui/umd/dist/components/Skeleton';

import { faArrowToBottom } from '@fortawesome/pro-regular-svg-icons/faArrowToBottom';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import throttle from 'lodash/throttle';

import useI18n from '@i18n';

import {
    IRange,
    ITransactionsFilterOption,
    ITransactionsFilterOptions,
    ITransactionsFilterValues,
    ITransactionsLoadings,
} from '@pages/statistics/transactions/interfaces';

import styles from '../../common/Filters/Currency.module.scss';

dayjs.extend(weekday);
dayjs.extend(localeData);

interface ITransactionsFilter {
    values: ITransactionsFilterValues;
    options: ITransactionsFilterOptions;
    loadings: ITransactionsLoadings;
    minMaxDateRange: {
        minDate: Date;
        maxDate: Date;
    };
    onChange: (fieldName: string, value: unknown) => void;
    onDateRangeChange: (newValue: string, range?: IRange) => void;
    onButtonClick: () => void;
    exportOnClick: () => void;
    exportInProgress: boolean;
    onSearch: (value: string) => void;
    nextPageCreatives: () => void;
}

// const { useBreakpoint } = Grid;

const Filters: React.FC<ITransactionsFilter> = ({
    values,
    onChange,
    options,
    loadings,
    minMaxDateRange,
    onDateRangeChange,
    exportOnClick,
    onButtonClick,
    exportInProgress,
    nextPageCreatives,
    onSearch,
}) => {
    const { offers, creatives, currencies, dateRanges, subs } = options;
    const { offerId, creativeId, currency, tsFrom, tsTo, datePeriod, subId, clickId } = values;
    const { isLoadingOffers, isLoadingCreatives, isLoadingSubs } = loadings;
    const { maxDate, minDate } = minMaxDateRange;

    const { t } = useI18n('Transactions');
    const { t: tStat } = useI18n('Statistic');
    const { t: tCommon } = useI18n('Common');

    const screens = useBreakpoint();

    const getValueSubs = (
        selectOptons: ITransactionsFilterOption<string>[],
        optionId: string,
    ): ITransactionsFilterOption<string> =>
        selectOptons?.find(option => option?.value === optionId?.toString()) || {
            value: null,
            label: t('All subs'),
        };

    const getValueOffers = (
        selectOptons: ITransactionsFilterOption<number>[],
        optionId: string,
    ): ITransactionsFilterOption<number> =>
        selectOptons?.find(option => +option?.value === +optionId) || {
            value: null,
            label: tStat('All offers'),
        };

    const getCreativesValue = (
        selectOptions: ITransactionsFilterOption<string>[],
        optionId: string,
    ): ITransactionsFilterOption<string | number> =>
        selectOptions?.find(option => option?.value === optionId?.toString()) || {
            value: -1,
            label: tStat('All creatives'),
        };

    const onScrollBottom = throttle(({ target }, handler) => {
        const isScrolledToBottom = target.scrollHeight - target.scrollTop === target.clientHeight;

        if (isScrolledToBottom) handler();
    }, 100);

    const selectFilter = (inputValue, option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase());

    return (
        <div className="mb-8 xs:p-4 xs:shadow-1 md:p-6 rounded-[6px]">
            <div className="flex flex-wrap gap-2 xs:gap-4 xl:max-w-[1100px]">
                <div className="w-full sm:w-auto sm:min-w-[412px]">
                    <Radio.Group
                        onChange={e => onDateRangeChange(e.target.value)}
                        value={datePeriod}
                        buttonStyle="solid"
                        size={screens.xs ? 'small' : 'middle'}
                        block
                    >
                        {dateRanges.map(item => (
                            <Radio.Button key={item.key} value={item.value}>
                                {item.label}
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                </div>
                <div className="w-full sm:w-auto sm:flex-1 basis-auto min-w-[260px]">
                    <DatePickerRange
                        defaultValue={[dayjs(tsFrom), dayjs(tsTo)]}
                        maxDate={dayjs(maxDate)}
                        minDate={dayjs(minDate)}
                        onChange={dates => {
                            if (dates) {
                                const today = new Date();
                                const [from, to] = dates || [today, today];
                                onChange('dateRange', {
                                    dateFrom: from,
                                    dateTo: to,
                                });
                                onDateRangeChange(null, {
                                    tsFrom: dayjs(from).toDate(),
                                    tsTo: dayjs(to).toDate(),
                                });
                            } else onDateRangeChange('today');
                        }}
                        format="DD.MM.YYYY"
                        value={[dayjs(tsFrom), dayjs(tsTo)]}
                        className="w-full"
                        id="dateRange"
                        placeholder={[t('Start date'), t('End date')]}
                    />
                </div>
                <div className="w-full xs:w-[48.7%] sm:w-[auto]">
                    <Skeleton loading={isLoadingOffers} className={styles.customSkeleton}>
                        <Radio.Group
                            onChange={e => onChange('currency', e.target.value)}
                            value={currency}
                            buttonStyle="solid"
                            size={screens.xs ? 'small' : 'middle'}
                            block={!screens.md}
                        >
                            {currencies.map(item => (
                                <Radio.Button value={item.value} key={item.value}>
                                    {item.label}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    </Skeleton>
                </div>
                <div className="w-full sm:flex-1 basis-auto xl:flex-none xl:w-[208px] lg:w-[193px] md:w-[163px] sm:min-w-[184px] xs:w-[48%]">
                    <Select
                        id="StatisticsCreativesSelect"
                        className="w-full"
                        options={creatives}
                        value={getCreativesValue(creatives, creativeId)}
                        showSearch
                        onPopupScroll={e => onScrollBottom(e, nextPageCreatives)}
                        onChange={value => onChange('creativeId', value)}
                        onSearch={inputValue => onSearch(inputValue)}
                        loading={isLoadingCreatives}
                        placeholder={tStat('All creatives')}
                        notFoundContent={tCommon('Nothing found')}
                    />
                </div>
                <div className="w-full sm:flex-1 basis-auto xl:flex-none xl:w-[208px] lg:w-[193px] md:w-[163px] sm:min-w-[184px] xs:w-[48.7%]">
                    <Select
                        id="FiltersOffers"
                        className="w-full"
                        value={getValueOffers(offers, offerId)}
                        loading={isLoadingOffers}
                        onChange={(_, option) => onChange('offerId', option)}
                        options={offers}
                        placeholder={tStat('All offers')}
                        filterOption={selectFilter}
                        notFoundContent={tCommon('Nothing found')}
                    />
                </div>
                <div className="w-full sm:flex-1 basis-auto xl:flex-none xl:w-[208px] lg:w-[193px] md:w-[163px] sm:min-w-[184px] xs:w-[48%]">
                    <Select
                        id="FiltersSubs"
                        className="w-full"
                        value={getValueSubs(subs, subId === null ? subId : String(subId))}
                        loading={isLoadingSubs}
                        onChange={value => onChange('subId', value)}
                        options={subs}
                        placeholder={t('All SUB')}
                        notFoundContent={tCommon('Nothing found')}
                    />
                </div>
                <div className="w-full sm:flex-1 basis-auto xl:flex-none xl:w-[208px] lg:w-[193px] md:w-[163px] sm:min-w-[184px]">
                    <Input
                        id=""
                        placeholder="Click ID"
                        value={clickId}
                        onChange={e => onChange('clickId', e.target.value)}
                    />
                </div>
                <div className="flex flex-wrap w-full lg:gap-6 gap-3">
                    <Button
                        onClick={onButtonClick}
                        icon={<FontAwesomeIcon icon={faSearch} />}
                        type="primary"
                        className="w-full xs:w-[49%] sm:w-auto"
                    >
                        {t('Find')}
                    </Button>
                    {exportInProgress ? (
                        <Button>...</Button>
                    ) : (
                        <Button
                            onClick={exportOnClick}
                            icon={<FontAwesomeIcon icon={faArrowToBottom} />}
                            iconPosition="start"
                            className="w-full xs:w-[48%] sm:w-auto"
                        >
                            {t('Export')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Filters;
